import { call, put, select, take } from 'redux-saga/effects';
import {
  fetchAllExcelTransactionsRequest,
  saveExcelDrawerTransactionsBatchFailure,
  saveExcelDrawerTransactionsBatchSuccess,
  setTransactionsReviewedRequest,
  uploadReceiptToProvider,
} from '../actions';
import {
  updateExcelTransaction,
  UpdateExcelTransactionResponse,
} from '../api';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import {
  SaveExcelDrawerTransactionsBatchItem,
  SaveExcelDrawerTransactionsBatchRequestAction,
  SaveExcelDrawerTransactionsBatchRequestActionPayload,
} from '../types';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { turnMessageOn } from 'store/app/actions';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { buildTransactionsRefreshFilters } from 'utils/entityTransactionsRequestFilters';
import i18n from 'i18next';
import {
  SET_TRANSACTIONS_REVIEWED_FAILURE,
  SET_TRANSACTIONS_REVIEWED_SUCCESS,
} from '../constants';
import { getDrawerTransactionIdMap } from '../selectors';
import {
  appendDrawerTransactionIdHops,
  resolveDrawerTransactionId,
  type DrawerTransactionIdHop,
} from '../drawerTransactionIdMap';

const updateTransactionsPromises = async (
  organisationId: string,
  items: Array<{ transactionUuid: string; body: any }>
) => {
  return await Promise.allSettled(
    items.map((item) =>
      updateExcelTransaction(organisationId, item.transactionUuid, item.body)
    )
  );
};

function resolveBatchItem(
  item: SaveExcelDrawerTransactionsBatchItem,
  idMap: Record<string, string>
): SaveExcelDrawerTransactionsBatchItem {
  const resolvedUuid = resolveDrawerTransactionId(item.transactionUuid, idMap);
  const body = item.body as { uuid?: string } | undefined;
  return {
    ...item,
    transactionUuid: resolvedUuid,
    body:
      body && body.uuid != null
        ? { ...item.body, uuid: resolvedUuid }
        : item.body,
  };
}

function* postFileAfterDrawerSave(
  organisationId: string,
  refreshParams: SaveExcelDrawerTransactionsBatchRequestActionPayload['refreshParams']
) {
  const fileId = refreshParams?.postAfterSave?.fileId?.trim();
  if (!organisationId?.trim() || !fileId) return;
  yield put(
    uploadReceiptToProvider(
      organisationId,
      [fileId],
      refreshParams.postAfterSave?.status,
      refreshParams.postAfterSave?.bankAccountId
    )
  );
}

export function* saveExcelDrawerTransactionsBatchSaga(
  action: SaveExcelDrawerTransactionsBatchRequestAction
) {
  try {
    const { organisationId, items, refreshParams } = action.payload;
    const idMap: Record<string, string> = yield select(getDrawerTransactionIdMap);

    const reviewIds: string[] = Array.from(
      new Set(
        [
          ...(refreshParams?.markAsReviewedIds ?? []),
          ...(items ?? [])
            .filter((i) => i.markAsReviewed)
            .map((i) => i.transactionUuid),
        ]
          .filter(Boolean)
          .map((id) => resolveDrawerTransactionId(String(id), idMap))
      )
    );

    const uniqueItems = Array.from(
      new Map(
        (items ?? [])
          .map((item) => resolveBatchItem(item, idMap))
          .map((item) => [String(item.transactionUuid), item] as const)
      ).values()
    );

    if (
      !organisationId?.trim() ||
      (!uniqueItems.length && reviewIds.length === 0)
    ) {
      yield* postFileAfterDrawerSave(organisationId, refreshParams);
      yield put(saveExcelDrawerTransactionsBatchSuccess());
      return;
    }

    const idHops: DrawerTransactionIdHop[] = [];

    if (uniqueItems.length) {
      const responses: PromiseSettledResult<UpdateExcelTransactionResponse>[] =
        yield call(updateTransactionsPromises, organisationId, uniqueItems);

      for (let i = 0; i < responses.length; i++) {
        const result = responses[i];
        if (result.status === 'rejected') {
          throw new Error(result.reason);
        }

        const response = result.value;
        let err: ApiError | null = checkResponse(response?.responseMessage);
        if (err) {
          if (
            response.data?.validationErrors &&
            response.data.validationErrors.length > 0
          ) {
            const fieldNames = response.data.validationErrors.filter(Boolean);
            const separator = err.message.endsWith('.') ? ' ' : '. ';
            if (fieldNames.length === 1) {
              err.message += `${separator}${fieldNames[0]} is a required field`;
            } else if (fieldNames.length > 1) {
              err.message += `${separator}${fieldNames.join(
                ', '
              )} are required fields`;
            }
          }
          throw err;
        }

        const fromTransactionId = String(
          uniqueItems[i]?.transactionUuid ?? ''
        ).trim();
        const toTransactionId = String(
          response?.data?.newTransactionId ?? ''
        ).trim();
        if (
          fromTransactionId &&
          toTransactionId &&
          toTransactionId !== fromTransactionId
        ) {
          idHops.push({ fromTransactionId, toTransactionId });
        }
      }
    }

    const mapAfterSave = appendDrawerTransactionIdHops(idMap, idHops);
    const resolvedReviewIds = Array.from(
      new Set(
        reviewIds.map((id) => resolveDrawerTransactionId(id, mapAfterSave))
      )
    );

    let didMarkAsReviewed = false;
    let reviewFailed = false;
    if (resolvedReviewIds.length > 0) {
      didMarkAsReviewed = true;
      yield put(
        setTransactionsReviewedRequest(
          organisationId,
          resolvedReviewIds,
          refreshParams.page,
          refreshParams.pageSize,
          refreshParams.filters,
          refreshParams.tabFileImportIds,
          true
        )
      );
      const reviewOutcome: { type: string } = yield take([
        SET_TRANSACTIONS_REVIEWED_SUCCESS,
        SET_TRANSACTIONS_REVIEWED_FAILURE,
      ]);
      reviewFailed = reviewOutcome.type === SET_TRANSACTIONS_REVIEWED_FAILURE;
    }

    if (!didMarkAsReviewed) {
      const refreshFilters = buildTransactionsRefreshFilters({
        tableFilters: refreshParams?.filters,
        tabFileImportIds: refreshParams?.tabFileImportIds,
      });
      yield put(
        fetchAllExcelTransactionsRequest(
          organisationId,
          refreshParams.page,
          refreshParams.pageSize,
          refreshFilters
        )
      );
    }

    const successDescription =
      uniqueItems.length === 0 && didMarkAsReviewed
        ? resolvedReviewIds.length === 1
          ? 'Transaction marked as reviewed.'
          : `${resolvedReviewIds.length} transactions marked as reviewed.`
        : uniqueItems.length === 1
        ? didMarkAsReviewed
          ? 'Transaction saved and marked as reviewed.'
          : 'Transaction saved successfully.'
        : !didMarkAsReviewed
        ? `${uniqueItems.length} transactions saved successfully.`
        : resolvedReviewIds.length === uniqueItems.length
        ? `${uniqueItems.length} transactions saved and marked as reviewed.`
        : `${uniqueItems.length} transactions saved successfully. ${resolvedReviewIds.length} marked as reviewed.`;

    const willPost = Boolean(
      !reviewFailed && refreshParams?.postAfterSave?.fileId?.trim()
    );
    if (!reviewFailed && !willPost) {
      const successMessage: MessageData = {
        title: i18n.t('common.success'),
        description: successDescription,
        type: MessageStates.SUCCESS,
      };
      yield put(turnMessageOn(successMessage));
    }
    if (willPost) {
      yield* postFileAfterDrawerSave(organisationId, refreshParams);
    }
    yield put(
      saveExcelDrawerTransactionsBatchSuccess(
        idHops.length > 0 ? { idHops } : undefined
      )
    );
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(saveExcelDrawerTransactionsBatchFailure());
  }
}
